import type { KeyedMutator } from 'swr';
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Loader2, Settings2, AlertTriangle } from 'lucide-react';
import { FormBuilder } from '@/components/onboarding/form-builder';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community, OnboardingFormConfig } from '@/types';

interface OnboardingSettingsProps {
  communityId: string;
  mutateCommunity: KeyedMutator<{ community: Community }>;
  onboardingFormEnabled: boolean;
  setOnboardingFormEnabled: (val: boolean) => void;
  onboardingForm: OnboardingFormConfig | null;
  setOnboardingForm: (val: OnboardingFormConfig | null) => void;
  /** When true (Privacy → require approval), form builder shows per-question “before approval” toggles. */
  requiresApproval: boolean;
}

export function OnboardingSettings({
  communityId,
  mutateCommunity,
  onboardingFormEnabled,
  setOnboardingFormEnabled,
  onboardingForm,
  setOnboardingForm,
  requiresApproval,
}: OnboardingSettingsProps) {
  const [isFormBuilderOpen, setIsFormBuilderOpen] = useState(false);
  const [draftForm, setDraftForm] = useState<OnboardingFormConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const updateSetting = async (key: string, value: unknown) => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        [key]: value,
      });
      await mutateCommunity();
      toast.success('Settings updated');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update settings');
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Initialize draft with current form when opening
      setDraftForm(onboardingForm);
    } else {
      // Reset draft when closing
      setDraftForm(null);
    }
    setIsFormBuilderOpen(open);
  };

  const handleFormChange = (newForm: OnboardingFormConfig) => {
    // Update local draft state only, don't save to backend
    setDraftForm(newForm);
  };

  const handleSave = async () => {
    if (!draftForm) return;

    setIsSaving(true);
    try {
      setOnboardingForm(draftForm);
      await updateSetting('onboardingForm', draftForm);
      setIsFormBuilderOpen(false);
      setDraftForm(null);
    } catch (error) {
      console.error(error);
      // toast error handled in updateSetting
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraftForm(null);
    setIsFormBuilderOpen(false);
  };

  const handleResetOnboarding = async () => {
    if (
      !confirm(
        'Reset additional details for all members? They will be asked to provide their answers again.'
      )
    ) {
      return;
    }

    setIsResetting(true);
    try {
      const response = await axiosInstance.post(`/api/communities/${communityId}/reset-onboarding`);
      await mutateCommunity();
      toast.success(`Additional details reset for ${response.data.affectedCount} members`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to reset additional details');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional details</CardTitle>
        <CardDescription>
          Extra questions members answer when joining this community.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-2">
          <Label htmlFor="onboarding-mode" className="flex flex-col space-y-1">
            <span>Ask for additional details</span>
            <span className="font-normal text-xs text-muted-foreground">
              New members answer your custom questions before or as they join.
            </span>
          </Label>
          <Switch
            id="onboarding-mode"
            checked={onboardingFormEnabled}
            onCheckedChange={(checked) => {
              setOnboardingFormEnabled(checked);
              updateSetting('onboardingFormEnabled', checked);
            }}
          />
        </div>

        {onboardingFormEnabled && (
          <div className="pt-4 border-t border-border">
            <div className="bg-accent/5 rounded-xl p-6 border border-border flex flex-col items-center justify-center text-center space-y-4">
              <div className="p-3 bg-primary/10 text-primary rounded-full">
                <Settings2 className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-bold text-lg">Your questions</h4>
                <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                  Add steps and questions to collect the details you care about.
                </p>
              </div>

              <Dialog open={isFormBuilderOpen} onOpenChange={handleOpenChange}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    Edit questions
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0 gap-0">
                  <DialogHeader className="p-6 pb-2">
                    <DialogTitle>Additional details builder</DialogTitle>
                    <DialogDescription>
                      Add steps and questions for additional member details. Click Save to apply
                      changes.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex-1 overflow-auto px-6">
                    <FormBuilder
                      initialConfig={draftForm || undefined}
                      onChange={handleFormChange}
                      showBeforeApprovalToggle={requiresApproval}
                    />
                  </div>
                  <div className="p-6 pt-4 border-t border-border flex justify-end gap-3">
                    <Button variant="outline" onClick={handleDiscard} disabled={isSaving}>
                      Discard Changes
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving} className="gap-2">
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Reset additional-details completion */}
            <div className="mt-6 bg-amber-500/5 rounded-xl p-4 border border-amber-500/20">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3 flex-1">
                  <div className="p-2 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-lg h-fit">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                      Reset everyone&apos;s additional details
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Use this after changing questions so all joined members can answer again. They
                      will see a prompt when they visit the community.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-amber-500/50 hover:bg-amber-500/10 gap-2 shrink-0"
                  onClick={handleResetOnboarding}
                  disabled={isResetting}
                >
                  {isResetting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Reset All Members
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
